import { TargetingValues } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import { getType } from 'typesafe-actions';

import { logoutAsync, NetworkRequestState } from '@bolteu/redux-session';

import {
  requestTargetingParametersAsyncAction,
  TargetingParametersActions,
  type TargetingSessionContext,
} from './actions';

export interface TargetingParametersState {
  requestId: number;
  hasCompletedRequest: boolean;
  parameters: TargetingValues | null;
  fetchingState: NetworkRequestState;
  isDoingSafeRefresh: boolean;
  confirmedSession: TargetingSessionContext | null;
  error?: Error;
}

const initialState: TargetingParametersState = {
  requestId: 0,
  hasCompletedRequest: false,
  parameters: null,
  fetchingState: NetworkRequestState.unset,
  isDoingSafeRefresh: false,
  confirmedSession: null,
};

export const targetingParametersReducer = (
  state: TargetingParametersState = initialState,
  action: TargetingParametersActions,
): TargetingParametersState => {
  switch (action.type) {
    case getType(requestTargetingParametersAsyncAction.request): {
      return {
        ...state,
        requestId: state.requestId + 1,
        hasCompletedRequest: action.payload.safeRefresh ? state.hasCompletedRequest : false,
        parameters: action.payload.safeRefresh ? state.parameters : null,
        confirmedSession: null,
        error: undefined,
        isDoingSafeRefresh: !!action.payload.safeRefresh,
        fetchingState: NetworkRequestState.waiting,
      };
    }
    case getType(requestTargetingParametersAsyncAction.success): {
      if (
        state.fetchingState !== NetworkRequestState.waiting ||
        action.meta.requestId !== state.requestId
      ) {
        return state;
      }
      return {
        ...state,
        hasCompletedRequest: true,
        parameters: action.payload.parameters,
        confirmedSession: action.meta.session,
        isDoingSafeRefresh: false,
        fetchingState: NetworkRequestState.success,
      };
    }
    case getType(requestTargetingParametersAsyncAction.failure): {
      if (state.fetchingState !== NetworkRequestState.waiting || action.meta !== state.requestId) {
        return state;
      }
      return {
        ...state,
        isDoingSafeRefresh: false,
        fetchingState: NetworkRequestState.failure,
        confirmedSession: null,
        error: action.payload,
        hasCompletedRequest: true,
      };
    }

    case getType(requestTargetingParametersAsyncAction.cancel): {
      if (
        state.fetchingState !== NetworkRequestState.waiting ||
        action.payload !== state.requestId
      ) {
        return state;
      }
      return {
        ...state,
        fetchingState: NetworkRequestState.failure,
        isDoingSafeRefresh: false,
        confirmedSession: null,
        hasCompletedRequest: true,
      };
    }

    case getType(logoutAsync.request): {
      return { ...state, confirmedSession: null };
    }
    case getType(logoutAsync.success): {
      return {
        ...state,
        confirmedSession: null,
        isDoingSafeRefresh: false,
        hasCompletedRequest:
          state.hasCompletedRequest || state.fetchingState === NetworkRequestState.waiting,
        fetchingState:
          state.fetchingState === NetworkRequestState.waiting
            ? NetworkRequestState.failure
            : state.fetchingState,
      };
    }

    default: {
      return state;
    }
  }
};
